/**
 * =============================================================================
 * IDS CRM Web - Create Opportunity (sales rework Phase 3)
 * =============================================================================
 *
 * The single commercial entry point: route -> distributor -> customer/site ->
 * details -> line items (product lines with schedule prefill + special pricing
 * + mandatory internal reason; non-product free-text priced lines). "+ New
 * customer/distributor" hands off to the REAL customer form (/customers/new)
 * with a return_to round-trip; the in-progress deal is stashed in
 * sessionStorage and restored on return, so there is exactly one
 * create-customer workflow in the system. Submits ONE transactional
 * POST /opportunity-groups.
 *
 * Orders (spec part B, Gareth 2026-10-16): the first choice is Opportunity
 * or Order. An order is a purchase already agreed - its product lines start
 * on the product's order start stage (Contract Signed) and it is kept out of
 * the pipeline figures. The same people create both.
 *
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { DEFAULT_ASSET_CONFIG, getActiveOptions, normaliseAssetConfig } from '../utils/assetTypes';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import SparePartsPicker from '../components/SparePartsPicker';
import { useSparePartsList } from '../utils/sparePartsList';
import './CustomerForm.css';
import { formatMoney } from '../utils/money';
import './OpportunityCreate.css';
import './CommercialLayout.css';

const DRAFT_STORAGE_KEY = 'opportunity-create-draft';
// Parts orders (Gareth 2026-10-19): a Parts & Consumables line is ONE part,
// picked from the spare parts list or typed in as not listed, with a quantity
// and a price each. No free text: it lets too many errors in.
const PARTS_PRODUCT = 'parts_consumables';
const QUANTITY_RE = /^\d{1,4}(\.\d{1,2})?$/;

/** GBP pence in the customer's currency at the admin rate (null when no rate). */
export function convertFromGbp(gbpMinor, rateToGbp) {
  const rate = Number(rateToGbp);
  if (!(rate > 0)) return null;
  return Math.round(gbpMinor / rate);
}

/** quantity × price each, in GBP pence (prices always start in GBP). */
export function partLineTotal(quantity, priceMajor) {
  if (!QUANTITY_RE.test(String(quantity || '').trim()) || priceMajor === '' || priceMajor === null || priceMajor === undefined) return 0;
  const [whole, frac = ''] = String(quantity).trim().split('.');
  const hundredths = Number(whole + frac.padEnd(2, '0'));
  return Math.round((hundredths * Math.round(Number(priceMajor) * 100)) / 100);
}

const toMajor = (minor) => (minor || minor === 0 ? (minor / 100).toFixed(2) : '');
const toMinor = (major) => Math.round((Number(major) || 0) * 100);

let nextRowId = 1;

function emptyProductLine() {
  return {
    rowId: nextRowId++,
    line_type: 'product',
    product: '',
    schedule: [],
    defaultSchedule: [],
    source: 'none',
    isPlaceholder: true,
    firstStage: null,
    orderStartStage: null,
    part: null,
    quantity: '1',
    price_each_major: '',
    priceNote: '',
    special: false,
    special_price_reason: '',
    expected_close: ''
  };
}

function emptyNonProductLine() {
  return {
    rowId: nextRowId++,
    line_type: 'non_product',
    description: '',
    price_major: ''
  };
}

// What the line starts on: an order starts on the product's order start
// stage, which Product Configuration may not have set.
function startingStageLabel(line, isOrder) {
  if (!line.product) return '—';
  if (isOrder) return line.orderStartStage ? line.orderStartStage.stage_name : 'No order start stage set';
  return line.firstStage ? `${line.firstStage.stage_name} (${line.firstStage.default_probability_pct}%)` : '—';
}

/** Customer/distributor search picker (shared SearchSelect) with an add-new hand-off. */
function EntityPicker({ label, type, selected, onSelect, onAddNew, disabled, distributorId }) {
  if (selected) {
    return (
      <div className="form-group">
        <label>{label}</label>
        <div className="entity-selected">
          <span>{selected.business_name} {selected.currency ? `(${selected.currency})` : ''}</span>
          {!disabled && (
            <button type="button" className="btn-link" onClick={() => onSelect(null)}>Change</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="form-group entity-picker">
      <label>{label}</label>
      {/* kind, not an exact type: the customer search finds every end customer,
          Prospect, Active or Dormant (Gareth 2026-09-14), so a dormant farm's
          won deal can make it Active again. */}
      <CustomerSearchSelect
        id={`entity_picker_${type}`}
        kind={type === 'distributor' ? 'distributor' : 'customer'}
        distributorId={distributorId || null}
        minChars={1}
        value=""
        placeholder={`Search ${type === 'distributor' ? 'distributors' : 'customers'}…`}
        disabled={disabled}
        getOptionSub={(row) => `${row.customer_number} · ${row.currency}`}
        onSelect={(row) => { if (row) onSelect(row); }}
      />
      <button type="button" className="btn-link" disabled={disabled} onClick={onAddNew}>
        + New {type === 'distributor' ? 'distributor' : 'customer'}
      </button>
    </div>
  );
}

function OpportunityCreate() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { guardElement, markDirty, resetDirty } = useUnsavedChanges();

  const [kind, setKind] = useState(() => (searchParams.get('kind') === 'order' ? 'order' : 'opportunity'));
  const isOrder = kind === 'order';
  const recordWord = isOrder ? 'Order' : 'Opportunity';
  const [route, setRoute] = useState('direct');
  const [distributor, setDistributor] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [sites, setSites] = useState([]);
  const [farmSiteId, setFarmSiteId] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [owners, setOwners] = useState([]);
  const [products, setProducts] = useState([]);
  const [lineItems, setLineItems] = useState([emptyProductLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Farm-site selection to re-apply after the sites list reloads (draft restore).
  const pendingFarmSiteIdRef = useRef('');

  const currency = customer?.currency || 'GBP';
  const hasPartsLine = lineItems.some(line => line.line_type === 'product' && line.product === PARTS_PRODUCT);
  const spareParts = useSparePartsList({ authFetch, enabled: hasPartsLine });
  // Parts prices are typed in GBP and shown in the customer's currency at the
  // admin rate (Gareth 2026-10-19). undefined while unknown, null when no rate.
  const [fxRate, setFxRate] = useState(undefined);
  useEffect(() => {
    if (!hasPartsLine) return undefined;
    if (currency === 'GBP') { setFxRate('1'); return undefined; }
    let live = true;
    setFxRate(undefined);
    authFetch(`/api/v1/opportunity-groups/fx-rate?currency=${encodeURIComponent(currency)}`)
      .then(response => (response.ok ? response.json() : { rate_to_gbp: null }))
      .then(data => { if (live) setFxRate(data.rate_to_gbp || null); })
      .catch(() => { if (live) setFxRate(null); });
    return () => { live = false; };
  }, [authFetch, currency, hasPartsLine]);
  const inCustomerCurrency = (gbpMinor) => (currency === 'GBP' ? gbpMinor : convertFromGbp(gbpMinor, fxRate));

  // Round-trip draft: "+ New customer/distributor" hands off to the full
  // customer form; the stashed in-progress deal is restored here on return.
  useEffect(() => {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    try {
      const draft = JSON.parse(raw);
      if (draft.kind) setKind(draft.kind === 'order' ? 'order' : 'opportunity');
      if (draft.route) setRoute(draft.route);
      if (draft.distributor) setDistributor(draft.distributor);
      if (draft.customer) setCustomer(draft.customer);
      pendingFarmSiteIdRef.current = draft.farmSiteId || '';
      if (draft.title) setTitle(draft.title);
      if (draft.notes) setNotes(draft.notes);
      if (draft.ownerId) setOwnerId(draft.ownerId);
      if (Array.isArray(draft.lineItems) && draft.lineItems.length > 0) {
        setLineItems(draft.lineItems);
        // Keep new lines' rowIds clear of the restored ones.
        nextRowId = Math.max(nextRowId, ...draft.lineItems.map(line => (line.rowId || 0) + 1));
      }
      markDirty();
    } catch (draftError) {
      // A corrupt draft only costs the stash - the form still works.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load option data + honour ?customer_id= / ?distributor_id= prefill
  // (the return leg of the customer-form round-trip).
  useEffect(() => {
    const load = async () => {
      try {
        const [configRes, ownersRes] = await Promise.all([
          authFetch('/api/v1/assets/config'),
          authFetch('/api/v1/opportunities/assignable-users')
        ]);
        const configData = configRes.ok ? await configRes.json() : { data: DEFAULT_ASSET_CONFIG };
        setProducts(getActiveOptions(normaliseAssetConfig(configData.data).asset_types));
        if (ownersRes.ok) {
          const ownersData = await ownersRes.json();
          setOwners(ownersData.data || []);
        }
      } catch (err) {
        setProducts(DEFAULT_ASSET_CONFIG.asset_types);
      }

      const prefillDistributorId = searchParams.get('distributor_id');
      if (prefillDistributorId) {
        try {
          const response = await authFetch(`/api/v1/customers/${prefillDistributorId}`);
          if (response.ok) {
            setDistributor(await response.json());
            setRoute('distributor');
          }
        } catch (err) {
          // prefill is best-effort
        }
      }

      const prefillCustomerId = searchParams.get('customer_id');
      if (prefillCustomerId) {
        try {
          const response = await authFetch(`/api/v1/customers/${prefillCustomerId}`);
          if (response.ok) setCustomer(await response.json());
        } catch (err) {
          // прefill is best-effort
        }
      }
    };
    load();
    setOwnerId(user?.userId || '');
  }, [authFetch, searchParams, user?.userId]);

  // Sites follow the selected customer
  useEffect(() => {
    setFarmSiteId('');
    setSites([]);
    if (!customer) return;
    const loadSites = async () => {
      try {
        const response = await authFetch(`/api/v1/customers/${customer.customer_id}/sites`);
        if (response.ok) {
          const data = await response.json();
          const loadedSites = data.data || [];
          setSites(loadedSites);
          // Draft restore: re-select the stashed site if it still belongs here.
          if (pendingFarmSiteIdRef.current) {
            if (loadedSites.some(site => site.site_id === pendingFarmSiteIdRef.current)) {
              setFarmSiteId(pendingFarmSiteIdRef.current);
            }
            pendingFarmSiteIdRef.current = '';
          }
        }
      } catch (err) {
        setSites([]);
      }
    };
    loadSites();
  }, [customer, authFetch]);

  // Hand off to the real customer form; the deal draft survives the trip.
  const openNewEntityForm = (type) => {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
      kind, route, distributor, customer, farmSiteId, title, notes, ownerId, lineItems
    }));
    resetDirty();
    const formType = type === 'distributor' ? 'distributor' : 'prospect';
    navigate(`/customers/new?type=${formType}&return_to=${encodeURIComponent('/pipeline/new')}`);
  };

  const updateLine = (rowId, changes) => {
    markDirty();
    setLineItems(prev => prev.map(line => (line.rowId === rowId ? { ...line, ...changes } : line)));
  };

  const fetchDefaultPricing = useCallback(async (rowId, product, distributorId) => {
    if (!product) return;
    try {
      const params = new URLSearchParams({ product_type: product });
      if (distributorId) params.append('distributor_id', distributorId);
      const [pricingRes, stagesRes] = await Promise.all([
        authFetch(`/api/v1/product-pricing/resolve?${params}`),
        authFetch('/api/v1/opportunities/pipeline-stages')
      ]);
      const pricing = pricingRes.ok ? await pricingRes.json() : { schedule: [], source: 'none', is_placeholder: true };
      const stagesData = stagesRes.ok ? await stagesRes.json() : { data: {} };
      const productStages = stagesData.data?.[product] || [];
      const firstStage = productStages[0] || null;
      const orderStartStage = productStages.find(stage => stage.is_order_start) || null;
      setLineItems(prev => prev.map(line => (
        line.rowId === rowId
          ? {
            ...line,
            defaultSchedule: pricing.schedule || [],
            schedule: (pricing.schedule || []).map(entry => ({ ...entry })),
            source: pricing.source,
            isPlaceholder: pricing.is_placeholder !== false,
            firstStage,
            orderStartStage,
            special: false,
            special_price_reason: ''
          }
          : line
      )));
    } catch (err) {
      // Leave the line unpriced; server-side validation still applies.
    }
  }, [authFetch]);

  // Re-resolve pricing when the distributor changes (distributor-first pricing)
  useEffect(() => {
    lineItems
      .filter(line => line.line_type === 'product' && line.product)
      .forEach(line => fetchDefaultPricing(line.rowId, line.product, route === 'distributor' ? distributor?.customer_id : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributor?.customer_id, route]);

  const scheduleTotal = (schedule) => schedule
    .filter(entry => !entry.recurring)
    .reduce((sum, entry) => sum + (Number(entry.amount_minor_units) || 0), 0);

  const groupTotal = lineItems.reduce((sum, line) => {
    if (line.line_type === 'non_product') return sum + toMinor(line.price_major);
    if (line.product === PARTS_PRODUCT) return sum + (inCustomerCurrency(partLineTotal(line.quantity, line.price_each_major)) || 0);
    return sum + scheduleTotal(line.schedule);
  }, 0);

  // A part picked from the list fills in its price each (cost + markup, or the
  // item's own charge); it can be overwritten. Not listed starts empty.
  const pickPart = async (rowId, row) => {
    if (row.notInList) {
      updateLine(rowId, { part: { catalogue_id: null, part_name: row.name, part_number: '' }, price_each_major: '', priceNote: '' });
      return;
    }
    updateLine(rowId, { part: { catalogue_id: row.id, part_name: row.name, part_number: row.number }, price_each_major: '', priceNote: 'Looking up the list price…' });
    try {
      const response = await authFetch(`/api/v1/parts/catalogue/${row.id}/order-price`);
      const data = response.ok ? await response.json() : null;
      const priced = data && data.unit_price_gbp_minor_units !== null && data.unit_price_gbp_minor_units !== undefined;
      setLineItems(prev => prev.map(line => (line.rowId === rowId && line.part?.catalogue_id === row.id
        ? {
          ...line,
          price_each_major: priced ? toMajor(data.unit_price_gbp_minor_units) : line.price_each_major,
          priceNote: priced ? 'From the spare parts list. Change it if needed.' : 'The list has no price for this part. Enter the price each.'
        }
        : line)));
    } catch (err) {
      setLineItems(prev => prev.map(line => (line.rowId === rowId ? { ...line, priceNote: 'The list price could not be read. Enter the price each.' } : line)));
    }
  };

  const submit = async () => {
    setError('');
    if (!customer) return setError('Select a customer');
    if (route === 'distributor' && !distributor) return setError('Select a distributor');
    if (!title.trim()) return setError('Title is required');
    if (lineItems.length === 0) return setError('Add at least one line item');
    // An order of non-product lines alone shows nowhere (Gareth 2026-10-19).
    if (isOrder && !lineItems.some(line => line.line_type === 'product')) {
      return setError('An order needs at least one product line. For parts or consumables, choose the Parts & Consumables product.');
    }

    for (const line of lineItems) {
      if (line.line_type === 'product') {
        if (!line.product) return setError('Every product line needs a product');
        if (line.product === PARTS_PRODUCT) {
          if (!line.part || !String(line.part.part_name || '').trim()) {
            return setError('Choose a part from the spare parts list, or type a part not in the list, on every Parts & Consumables line');
          }
          const quantity = String(line.quantity || '').trim();
          if (!QUANTITY_RE.test(quantity) || Number(quantity) <= 0) {
            return setError('Every part needs a quantity above 0, with at most 2 decimal places');
          }
          if (line.price_each_major === '' || !(Number(line.price_each_major) >= 0)) {
            return setError('Every part needs a price each in GBP (0 or more)');
          }
          if (currency !== 'GBP' && !fxRate) {
            return setError(`No exchange rate is set for ${currency}, so the GBP price cannot be converted. Ask an admin to set it in Settings.`);
          }
        }
        if (line.special && !line.special_price_reason.trim()) {
          return setError('Special pricing needs an internal reason on every changed line');
        }
      } else {
        if (!line.description.trim()) return setError('Every non-product line needs a description');
        if (line.price_major === '' || Number(line.price_major) < 0) {
          return setError('Every non-product line needs a price');
        }
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        kind,
        route,
        customer_id: customer.customer_id,
        distributor_id: route === 'distributor' ? distributor.customer_id : null,
        farm_site_id: farmSiteId || null,
        title: title.trim(),
        notes: notes || null,
        owner_id: ownerId || null,
        line_items: lineItems.map(line => (
          line.line_type === 'product'
            ? {
              line_type: 'product',
              product: line.product,
              schedule: line.schedule,
              special_price_reason: line.special ? line.special_price_reason.trim() : null,
              expected_close: line.expected_close || null,
              ...(line.product === PARTS_PRODUCT ? {
                part: {
                  catalogue_id: line.part?.catalogue_id || null,
                  part_name: String(line.part?.part_name || '').trim(),
                  part_number: String(line.part?.part_number || '').trim() || null,
                  quantity: String(line.quantity || '').trim(),
                  unit_price_gbp_minor_units: toMinor(line.price_each_major)
                }
              } : {})
            }
            : {
              line_type: 'non_product',
              description: line.description.trim(),
              price_minor_units: toMinor(line.price_major)
            }
        ))
      };

      const response = await authFetch('/api/v1/opportunity-groups', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `Failed to create the ${recordWord.toLowerCase()}`);
      }
      const data = await response.json();
      resetDirty();
      const approvals = data.data?.approvals_raised || 0;
      navigate(`/pipeline/groups/${data.data.group_id}`, {
        state: {
          toast: approvals > 0
            ? `${data.data.group_ref} created — ${approvals} line item${approvals === 1 ? '' : 's'} sent for approval`
            : `${data.data.group_ref} created`
        }
      });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="customer-form-page opportunity-create-page">
      {guardElement}
      <div className="page-header">
        <div>
          <h1>New {recordWord}</h1>
          <p>
            {isOrder
              ? 'A purchase already agreed — no quoting or survey. Each line starts at the product’s order start stage and is kept out of the pipeline figures.'
              : 'One grouped commercial record — each line item moves through its own product pipeline.'}
          </p>
        </div>
        <BackToList to="/pipeline" className="btn-secondary">Cancel</BackToList>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="form-section">
        <h2>Type</h2>
        <div className="route-toggle" role="group" aria-label="Opportunity or order">
          {[['opportunity', 'Opportunity'], ['order', 'Order']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={kind === value ? 'route-btn active' : 'route-btn'}
              aria-pressed={kind === value}
              onClick={() => { setKind(value); markDirty(); }}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="form-hint">
          {isOrder
            ? 'An order is a purchase already agreed, e.g. a distributor orders a robot system or a farm orders parts.'
            : 'A deal being worked: it starts at the first stage and counts in the pipeline.'}
        </p>
      </div>

      <div className="form-section">
        <h2>Route</h2>
        <div className="route-toggle">
          {['direct', 'distributor'].map(option => (
            <button
              key={option}
              type="button"
              className={route === option ? 'route-btn active' : 'route-btn'}
              onClick={() => { setRoute(option); markDirty(); }}
            >
              {option === 'direct' ? 'Direct' : 'Distributor'}
            </button>
          ))}
        </div>
        {route === 'distributor' && (
          <EntityPicker
            label="Distributor *"
            type="distributor"
            selected={distributor}
            onSelect={(row) => {
              setDistributor(row);
              // A different distributor means a different customer pool -
              // drop any customer already picked.
              setCustomer(null);
              markDirty();
            }}
            onAddNew={() => openNewEntityForm('distributor')}
          />
        )}
      </div>

      <div className="form-section">
        <h2>Customer &amp; Site</h2>
        <EntityPicker
          label="Customer *"
          type="customer"
          selected={customer}
          distributorId={route === 'distributor' ? distributor?.customer_id : null}
          onSelect={(row) => { setCustomer(row); markDirty(); }}
          onAddNew={() => openNewEntityForm('customer')}
        />
        {customer && sites.length > 0 && (
          <div className="form-group">
            <label>Site</label>
            <select value={farmSiteId} onChange={(e) => { setFarmSiteId(e.target.value); markDirty(); }}>
              <option value="">No specific site</option>
              {sites.map(site => (
                <option key={site.site_id} value={site.site_id}>{site.site_name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="form-section">
        <h2>Details</h2>
        <div className="form-group">
          <label>Title *</label>
          <input value={title} onChange={(e) => { setTitle(e.target.value); markDirty(); }} placeholder="e.g. Hillcrest robot + data package" />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={notes} rows={3} onChange={(e) => { setNotes(e.target.value); markDirty(); }} />
        </div>
        <div className="form-group">
          <label>Owner</label>
          <select value={ownerId} onChange={(e) => { setOwnerId(e.target.value); markDirty(); }}>
            {owners.map(owner => (
              <option key={owner.user_id} value={owner.user_id}>{owner.full_name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-section">
        <h2>Line Items</h2>
        {lineItems.map((line, index) => (
          <div key={line.rowId} className="line-item-card">
            <div className="line-item-head">
              <strong>Line {index + 1} — {line.line_type === 'product' ? 'Product' : 'Non-product'}</strong>
              {lineItems.length > 1 && (
                <button type="button" className="btn-danger btn-small" onClick={() => { markDirty(); setLineItems(prev => prev.filter(l => l.rowId !== line.rowId)); }}>
                  Remove
                </button>
              )}
            </div>

            {line.line_type === 'product' ? (
              <>
                <div className="form-row compact">
                  <div className="form-group">
                    <label>Product *</label>
                    <select
                      value={line.product}
                      onChange={(e) => {
                        const product = e.target.value;
                        updateLine(line.rowId, { product });
                        fetchDefaultPricing(line.rowId, product, route === 'distributor' ? distributor?.customer_id : null);
                      }}
                    >
                      <option value="">Select…</option>
                      {products.map(product => (
                        <option key={product.value} value={product.value}>{product.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Target close</label>
                    <input type="date" value={line.expected_close} onChange={(e) => updateLine(line.rowId, { expected_close: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Starting stage</label>
                    <input value={startingStageLabel(line, isOrder)} disabled />
                  </div>
                </div>

                {line.product === PARTS_PRODUCT && (
                  <div className="parts-order-line">
                    {line.part ? (
                      <div className="parts-order-chosen">
                        <span>
                          <strong>{line.part.part_name}</strong>
                          {line.part.catalogue_id
                            ? <span className="parts-order-number"> · {line.part.part_number}</span>
                            : <em className="placeholder-badge"> not in the list</em>}
                        </span>
                        <button type="button" className="btn-link" onClick={() => updateLine(line.rowId, { part: null, price_each_major: '', priceNote: '' })}>
                          Change part
                        </button>
                      </div>
                    ) : (
                      <>
                        <label className="parts-order-picker-label" htmlFor={`parts_pick_${line.rowId}`}>Part * <small>(one part per line)</small></label>
                        <SparePartsPicker
                          id={`parts_pick_${line.rowId}`}
                          items={spareParts.items}
                          status={spareParts.status}
                          onPick={(row) => pickPart(line.rowId, row)}
                        />
                      </>
                    )}
                    {line.part && !line.part.catalogue_id && (
                      <div className="form-group">
                        <label htmlFor={`parts_number_${line.rowId}`}>Item Number (if known)</label>
                        <input
                          id={`parts_number_${line.rowId}`}
                          maxLength={80}
                          value={line.part.part_number || ''}
                          onChange={(e) => updateLine(line.rowId, { part: { ...line.part, part_number: e.target.value } })}
                        />
                      </div>
                    )}
                    {line.part && (
                      <div className="form-row compact">
                        <div className="form-group">
                          <label htmlFor={`parts_qty_${line.rowId}`}>Quantity *</label>
                          <input
                            id={`parts_qty_${line.rowId}`}
                            inputMode="decimal"
                            value={line.quantity}
                            onChange={(e) => updateLine(line.rowId, { quantity: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`parts_price_${line.rowId}`}>Price each (GBP) *</label>
                          <input
                            id={`parts_price_${line.rowId}`}
                            type="number" min="0" step="0.01"
                            value={line.price_each_major}
                            onChange={(e) => updateLine(line.rowId, { price_each_major: e.target.value, priceNote: '' })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Line total (GBP)</label>
                          <input value={formatMoney(partLineTotal(line.quantity, line.price_each_major), 'GBP')} disabled />
                        </div>
                      </div>
                    )}
                    {line.part && currency !== 'GBP' && (
                      <p className="parts-order-converted">
                        {fxRate === undefined && 'Looking up the exchange rate…'}
                        {fxRate === null && `No exchange rate is set for ${currency}. Ask an admin to set it in Settings before creating the order.`}
                        {fxRate && line.price_each_major !== '' && (
                          <>
                            In {currency}: <strong>{formatMoney(convertFromGbp(toMinor(line.price_each_major), fxRate), currency)}</strong> each,{' '}
                            <strong>{formatMoney(convertFromGbp(partLineTotal(line.quantity, line.price_each_major), fxRate), currency)}</strong> for the line
                            <span className="parts-order-rate"> (admin rate: 1 {currency} = £{Number(fxRate)})</span>
                          </>
                        )}
                      </p>
                    )}
                    {line.priceNote && <p className="form-hint">{line.priceNote}</p>}
                  </div>
                )}

                {line.product && line.product !== PARTS_PRODUCT && (
                  <div className="line-pricing">
                    <div className="line-pricing-head">
                      <span>
                        Pricing — {line.source === 'distributor' ? 'distributor terms' : 'product default'}
                        {line.isPlaceholder && line.source !== 'distributor' && <em className="placeholder-badge"> placeholder</em>}
                      </span>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={line.special}
                          onChange={(e) => updateLine(line.rowId, {
                            special: e.target.checked,
                            schedule: e.target.checked ? line.schedule : line.defaultSchedule.map(entry => ({ ...entry })),
                            special_price_reason: e.target.checked ? line.special_price_reason : ''
                          })}
                        />
                        Special pricing
                      </label>
                    </div>
                    <table className="line-schedule-table">
                      <thead>
                        <tr><th>Stage</th><th>Amount</th><th>Type</th></tr>
                      </thead>
                      <tbody>
                        {line.schedule.length === 0 && (
                          <tr><td colSpan="3" className="empty-schedule">No pricing configured for this product type.</td></tr>
                        )}
                        {line.schedule.map((entry, entryIndex) => (
                          <tr key={entry.key || entryIndex}>
                            <td>{entry.label}</td>
                            <td>
                              {line.special ? (
                                <input
                                  type="number" min="0" step="0.01"
                                  value={toMajor(entry.amount_minor_units)}
                                  onChange={(e) => {
                                    const schedule = line.schedule.map((s, i) => (
                                      i === entryIndex ? { ...s, amount_minor_units: toMinor(e.target.value) } : s
                                    ));
                                    updateLine(line.rowId, { schedule });
                                  }}
                                />
                              ) : formatMoney(entry.amount_minor_units, currency)}
                            </td>
                            <td>{entry.recurring ? 'Monthly' : 'One-off'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {line.special && (
                      <div className="form-group">
                        <label>Internal reason for special pricing * <small>(internal — never shown to the customer)</small></label>
                        <textarea
                          rows={2}
                          value={line.special_price_reason}
                          onChange={(e) => updateLine(line.rowId, { special_price_reason: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="form-row compact">
                <div className="form-group non-product-desc">
                  <label>Description *</label>
                  <input value={line.description} onChange={(e) => updateLine(line.rowId, { description: e.target.value })} placeholder="e.g. Site preparation works" />
                </div>
                <div className="form-group">
                  <label>Price ({currency}) *</label>
                  <input type="number" min="0" step="0.01" value={line.price_major} onChange={(e) => updateLine(line.rowId, { price_major: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="line-item-actions">
          <button type="button" className="btn-secondary btn-small" onClick={() => { markDirty(); setLineItems(prev => [...prev, emptyProductLine()]); }}>
            + Product Line
          </button>
          <button type="button" className="btn-secondary btn-small" onClick={() => { markDirty(); setLineItems(prev => [...prev, emptyNonProductLine()]); }}>
            + Non-product Line
          </button>
          <span className="group-total">Total (one-off): {formatMoney(groupTotal, currency)}</span>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn-primary" disabled={submitting} onClick={submit}>
          {submitting ? 'Creating…' : `Create ${recordWord}`}
        </button>
      </div>

    </div>
  );
}

export default OpportunityCreate;
